import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import DatePicker from 'antd/lib/date-picker';
import 'antd/lib/date-picker/style/index.less';
import TimePicker from 'antd/lib/time-picker';
import 'antd/lib/time-picker/style/index.less';
import 'antd/lib/input/style/index.less';
import 'antd/lib/style/core/motion.less';
import { formatInTimeZone } from 'date-fns-tz';
import { convertTokens } from '@date-fns/upgrade/v2';
import { Tooltip } from '@material-ui/core';

import { gray2 } from 'utils/color-styles';

const FormDateSelector = ({
  input,
  defaultValue,
  allowClear = true,
  noDate,
  noTime,
  minDate,
  maxDate,
  placeholder,
  placeholderTime,
  helperText,
  isPreviousDateDisabled,
  isNextDateDisabled,
  disabled,
  timeZone,
  displayTimeZone,
}) => {
  const value = (input?.value && moment(input?.value)) || defaultValue || null;
  const minMoment = minDate ? moment(minDate) : null;
  const maxMoment = maxDate ? moment(maxDate) : null;

  const disabledHours = () => {
    if (!value) {
      return [];
    }
    let start = 0;
    let end = 24;

    if (minMoment && minMoment.isSame(value, 'day')) {
      start = minMoment.hours();
    }
    if (maxMoment && maxMoment.isSame(value, 'day')) {
      end = maxMoment.hours() + 1;
    }

    // If both min and max are set for the day, only allow [start, end)
    // If only one is set, the other will be 0 or 24, so disables before/after as needed
    return [..._.range(0, start), ..._.range(end, 24)];
  };

  const disabledMinutes = (hour) =>
    _.range(60).filter((minute) => {
      if (minMoment && hour === minMoment.hours()) {
        return minute < minMoment.minute() || (minute % 5 !== 0 && minute >= minMoment.minute());
      }

      if (maxMoment && hour === maxMoment.hours()) {
        return minute > maxMoment.minute() || (minute % 5 !== 0 && minute <= maxMoment.minute());
      }

      return minute % 5 !== 0;
    });

  const helperTextBlock = helperText && (
    <span style={{ opacity: 0.5, fontSize: 12, paddingLeft: 10 }}>{helperText}</span>
  );

  const onTimeChange = (time) => {
    if (time) {
      time.minutes(Math.ceil(time.minutes() / 5) * 5);

      const hoursDisabled = disabledHours();
      if (hoursDisabled.includes(time.hour())) {
        // When switching am/pm, select the first allowed hour if the current hour is disabled.
        const isPM = time.hour() >= 12;
        const allowedHours = _.range(0, 24).filter(
          (h) => !hoursDisabled.includes(h) && (isPM ? h >= 12 : h < 12)
        );
        if (allowedHours.length > 0) {
          if (maxMoment && time.isAfter(maxMoment, 'hour')) {
            time.hour(allowedHours[allowedHours.length - 1]); // last allowed hour
          } else if (minMoment && time.isBefore(minMoment, 'hour')) {
            time.hour(allowedHours[0]); // first allowed hour
          } else {
            time.hour(allowedHours[0]); // fallback, should rarely happen
          }
        }
      }
    }
    input.onChange(time);
  };

  if (noDate) {
    return (
      <div
        style={{
          display: 'inline-block',
          paddingRight: '20px',
          verticalAlign: 'top',
        }}
      >
        <TimePicker
          value={value}
          format="h:mm a"
          use12Hours
          placeholder={placeholderTime || placeholder}
          size="large"
          allowClear={allowClear}
          disabled={disabled}
          disabledHours={disabledHours}
          disabledMinutes={disabledMinutes}
          hideDisabledOptions
          onChange={onTimeChange}
          onSelect={onTimeChange}
        />
        {helperTextBlock}
      </div>
    );
  }

  const handleDisableDate = (day) => {
    if (!isPreviousDateDisabled && !isNextDateDisabled) {
      return false;
    }
    if (isPreviousDateDisabled && isNextDateDisabled) {
      return true;
    }
    if (isPreviousDateDisabled && !isNextDateDisabled) {
      return (
        moment(day).isBefore(minDate || moment.unix(1)) ||
        (maxDate ? moment(day).isAfter(maxDate) : false)
      );
    }
    if (!isPreviousDateDisabled && isNextDateDisabled) {
      return moment(day).isAfter(minDate || moment.unix(1));
    }
    return false;
  };

  const getTimeZone = () =>
    timeZone || (typeof Intl === 'object' && Intl.DateTimeFormat()?.resolvedOptions()?.timeZone);
  const getUTC = () =>
    timeZone
      ? formatInTimeZone(
          value ? new Date(value.format()) : Date.now(),
          timeZone,
          convertTokens('Z')
        )
      : moment().format('Z');

  return (
    <div style={{ display: 'flex' }}>
      <div
        style={{
          display: 'inline-block',
          paddingRight: '20px',
          verticalAlign: 'top',
        }}
      >
        <DatePicker
          value={value}
          format="YYYY-MM-DD"
          disabled={disabled}
          placeholder={placeholder}
          size="large"
          allowClear={allowClear}
          disabledDate={handleDisableDate}
          onChange={(date) => {
            if (date) {
              const now = moment();
              if (date.hours() === now.hours() && date.minutes() === now.minutes()) {
                date.hours(10);
                date.minutes(0);
              }
              input.onChange(date);
            } else {
              input.onChange(null);
            }
          }}
        />
      </div>
      {!noTime && (
        <div
          style={{
            display: 'inline-block',
            paddingRight: '20px',
            verticalAlign: 'top',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              height: 40,
              alignItems: 'center',
            }}
          >
            <TimePicker
              value={value}
              format="h:mm a"
              use12Hours
              placeholder={placeholderTime || placeholder}
              size="large"
              allowClear={allowClear}
              disabled={disabled}
              disabledHours={disabledHours}
              disabledMinutes={disabledMinutes}
              hideDisabledOptions
              onChange={onTimeChange}
              onSelect={onTimeChange}
            />

            {displayTimeZone && (
              <Tooltip title={getTimeZone()}>
                <div
                  style={{
                    marginTop: 5,
                    marginLeft: 20,
                    fontSize: 11,
                    color: gray2,
                    width: 70,
                  }}
                >
                  UTC{getUTC()}
                </div>
              </Tooltip>
            )}
          </div>
          {helperTextBlock}
        </div>
      )}
    </div>
  );
};

FormDateSelector.propTypes = {
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  defaultValue: PropTypes.object,
  input: PropTypes.object,
  placeholder: PropTypes.string,
  placeholderTime: PropTypes.string,
  helperText: PropTypes.string,
  noDate: PropTypes.bool,
  noTime: PropTypes.bool,
  allowClear: PropTypes.bool,
  isPreviousDateDisabled: PropTypes.bool,
  isNextDateDisabled: PropTypes.bool,
  disabled: PropTypes.bool,
  displayTimeZone: PropTypes.bool,
  timeZone: PropTypes.string,
};

export default FormDateSelector;
